/**
 * Canonical output-projection constants.
 *
 * These numbers are shared by two independent renderers:
 *   - the WebGL post-FX pass in `viz/src/viz/postFxPass.ts` (Production Scenes)
 *   - the WebGPU/TSL pipeline in `app/templates/particles.html` (Visual Deck)
 *
 * The two implementations are written in different shading languages and are
 * deployed separately, so they cannot import from each other at runtime. They
 * are kept in sync by `tests/test_output_projection_parity.py`, which parses
 * both files and fails if any constant drifts. Change a value here, mirror it
 * in `app/static/js/output-projection.js`, and the parity test will confirm.
 */

export type OutputProjectionName = "flat" | "curved" | "cylinder" | "sphere";

export const OUTPUT_PROJECTION_MODES: Record<OutputProjectionName, number> = {
  flat: 0,
  curved: 1,
  cylinder: 2,
  sphere: 3,
};

export const OUTPUT_PROJECTION = {
  /** Radial bulge applied per unit r^2 for curved LED walls. */
  curvedBulge: 0.24,
  /** Half-angle swept by a wraparound cylinder at fov 0 and fov 1, radians. */
  cylinderAngleMin: 0.08,
  cylinderAngleMax: 1.15,
  /**
   * Half-angles swept by the sphere patch at fov 0 and fov 1, radians.
   * At fov 1 this is roughly +/-63 degrees of longitude by +/-47 degrees of
   * latitude, i.e. a patch of the sphere rather than a full 360x180 panorama.
   * Equirectangular panorama content must therefore use flat output, not this.
   */
  sphereLonMin: 0.08,
  sphereLonMax: 1.1,
  sphereLatMin: 0.06,
  sphereLatMax: 0.82,
  /**
   * The sphere latitude extents above were authored against a 2:1 output. Any
   * other aspect rescales latitude relative to this reference so the same Fov
   * setting produces the same solid angle regardless of wall resolution.
   */
  sphereReferenceAspect: 2,
  /**
   * Hard ceiling on the aspect-corrected latitude half-angle, radians.
   * tan() diverges at pi/2 and flips sign past it, so an uncorrected square
   * output (aspect 1, which doubles the latitude extent) would otherwise push
   * maxLat to 1.64 rad and blank the image entirely.
   */
  sphereLatCeiling: 1.45,
  /** Half-width of the projected-edge falloff, in output uv units. */
  edgeFeather: 0.012,
} as const;

/** Formats a number as a GLSL float literal (always with a decimal point). */
export function glslFloat(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : String(value);
}
